import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {Box, Button, Flex, Text} from '@salesforce/retail-react-app/app/components/shared/ui';

import {
  CoverSlotCard,
  OptionStep,
  OptionStepRadio,
  SubtotalFooter,
} from '@amblique/king-living/app/components/complex-customize/option-step';
import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {getContainerBlockInfo} from '@amblique/king-living/app/utils/product-utils';
import {
  getCustomizeStepSlotKind,
  getStepTitle,
  getVisibleCustomizeSteps,
  isCustomizeOuterNextDisabled,
} from '@amblique/king-living/app/hooks/use-customize-steps';

/**
 * Renders the main steps list with all slots inline.
 * Shown when no sub-view (product picker) is active.
 */
export const StepsMainView = ({
  activeStepId,
  bodyHeight,
  contentBodyStyles,
  coverGroups,
  currency,
  enableLocking,
  footerProduct,
  galleryPreviewProduct,
  hasUserInteracted,
  isSimplePDP = false,
  isStepComplete,
  isMobile,
  liveMessage,
  onDone,
  onGalleryProductChange,
  onMainNext,
  onSelect,
  onSlotChoose,
  onSlotProductPreview,
  onStepInteraction,
  onViewDetails,
  product,
  stepsEverCompleteIds = [],
  scrollBodyRef,
  selections,
  selectionSummary,
  showSubtotalFooter,
  slotButtonRefs,
  stepRefs,
  steps,
  styles,
  subtotalListPrice,
  subtotalPrice,
}) => {
  const intl = useIntl();
  const visibleSteps = getVisibleCustomizeSteps(steps);
  const firstIncompleteVisibleIndex = visibleSteps.findIndex((step) => !isStepComplete(step.id));
  const allRequiredComplete = firstIncompleteVisibleIndex === -1;

  // Show Next only after the user has actively interacted (opened a sub-panel or made a selection).
  const showNextOnMain = !allRequiredComplete && hasUserInteracted;

  const isNextDisabled = isCustomizeOuterNextDisabled({
    steps,
    isStepComplete,
    activeStepId,
    stepsEverCompleteIds,
  });

  const showListPrice = subtotalListPrice > 0 && subtotalListPrice > (subtotalPrice ?? 0);

  const subtotalPriceText = subtotalPrice != null && subtotalPrice > 0 ? (
    <Flex alignItems="baseline" gap={1} wrap="wrap">
      <FormattedMessage
        defaultMessage="Package price {price}"
        id="customize_panel.footer.subtotal"
        values={{
          price: formatCurrencyHideTrailingZeroCents(intl, subtotalPrice, currency),
        }}
      />
      {showListPrice && (
        <Text as="span" color="greyscale.70" fontSize="xs" textDecoration="line-through">
          {formatCurrencyHideTrailingZeroCents(intl, subtotalListPrice, currency)}
        </Text>
      )}
    </Flex>
  ) : null;

  return (
    <>
      {/* Visually hidden live region for selection announcements */}
      <Box
        aria-live="polite"
        clip="rect(0,0,0,0)"
        height="1px"
        overflow="hidden"
        position="absolute"
        width="1px"
        aria-atomic
      >
        {liveMessage}
      </Box>

      <Box
        ref={scrollBodyRef}
        className="customize-steps-content-body"
        flex={1}
        height={bodyHeight}
        minHeight={0}
        overflowY="auto"
        paddingX={contentBodyStyles ? undefined : {base: 4, lg: 6}}
        paddingY={contentBodyStyles ? undefined : 4}
        sx={{WebkitOverflowScrolling: 'touch', touchAction: 'pan-y'}}
        {...styles.body}
        {...contentBodyStyles}
      >
        {steps.map((step, idx) => {
          // Filter invisible containers
          if (step.container?.isVisible === false) return null;

          // Filter empty containers (e.g. extra container with only optional add-ons)
          if (step.container?.values && step.container.values.length === 0) return null;

          // KINDW-1121: Simple PDP renders Choose Your Cover via the standalone CoverSwatchGrid
          // above CustomizeStepsInline (Freeform + Configurable Master). Skip the inline cover slot
          // card here so we don't double-render the section as the legacy "Cover / No selection
          // yet / Choose" card. Complex PDPs keep the slot-card UX (still managed in this view).
          if (isSimplePDP && step.id === 'cover') return null;

          // Check if step has visible/orderable products
          const stepHasData =
            step.id === 'cover' ?
              step.hasData(coverGroups) :
              step.hasData();

          // Don't render step at all if no visible/orderable products
          if (!stepHasData) {
            return null;
          }

          const previousVisible = getVisibleCustomizeSteps(steps.slice(0, idx));
          const allPreviousComplete = previousVisible.every((s) => isStepComplete(s.id));
          const isEnabled = !enableLocking || allPreviousComplete;
          const visibleIndex = visibleSteps.findIndex((s) => s.id === step.id);
          const disabledOpacity = isEnabled ? 1 : (visibleIndex === firstIncompleteVisibleIndex + 1 ? 0.7 : 0.4);
          const stepTitle = getStepTitle(step, intl);
          const customizeStepKind = getCustomizeStepSlotKind(step);

          return (
            <Box
              key={step.id}
              ref={(el) => {
                stepRefs.current[step.id] = el;
              }}
              className={`customize-steps-main-view-step customize-steps-main-view-step--${customizeStepKind}`}
              {...styles.stepSection}
              aria-disabled={!isEnabled || undefined}
              opacity={disabledOpacity}
            >
              {idx > 0 && (
                <Box className="customize-steps-step-divider" {...styles.stepDivider} />
              )}
              <Text as="p" {...styles.stepHeading}>
                {stepTitle}
              </Text>
              {step.id === 'cover' ? (
                <CoverSlotCard
                  customizeStepKind={customizeStepKind}
                  isEnabled={isEnabled}
                  selection={selections?.cover}
                  styles={styles}
                  registerButtonRef={(el) => {
                    slotButtonRefs.current['cover-0'] = el;
                  }}
                  onChoose={() => onSlotChoose('cover', 0)}
                />
              ) : step.layoutType === 'slot-cards' ? (
                // OptionStep: UI show list of slot cards, click on "Choose" button to open sub-view to select product
                <OptionStep
                  blockInfo={getContainerBlockInfo(product, step.container.containerId)}
                  container={step.container}
                  customizeStepKind={customizeStepKind}
                  isSimplePDP={isSimplePDP}
                  isStepDisabled={!isEnabled}
                  selections={selections?.[step.id] || []}
                  styles={styles}
                  registerChooseButtonRef={(slotIndex, el) => {
                    slotButtonRefs.current[`${step.id}-${slotIndex}`] = el;
                  }}
                  onSlotChoose={(slotIndex) => onSlotChoose(step.id, slotIndex)}
                />
              ) : (
                // OptionStepRadio: UI show list of radio options directly without sub-view
                <OptionStepRadio
                  blockInfo={getContainerBlockInfo(product, step.container.containerId)}
                  container={step.container}
                  customizeStepKind={customizeStepKind}
                  isSimplePDP={isSimplePDP}
                  isStepDisabled={!isEnabled}
                  selections={selections?.[step.id] || []}
                  stepId={step.id}
                  styles={styles}
                  onSelect={onSelect}
                  onSlotProductPreview={onSlotProductPreview}
                  onStepInteraction={onStepInteraction}
                  onViewDetails={onViewDetails}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {showSubtotalFooter && (
        <SubtotalFooter
          footerProduct={footerProduct}
          priceText={subtotalPriceText}
          selectionSummary={selectionSummary}
          styles={styles}
        >
          {showNextOnMain ? (
            <Button
              {...styles.subtotalDoneButton}
              aria-disabled={isNextDisabled}
              id="customize-next-btn"
              isDisabled={isNextDisabled}
              tabIndex={isNextDisabled ? -1 : 0}
              type="button"
              onClick={onMainNext}
            >
              <FormattedMessage defaultMessage="Next" id="customize_panel.footer.next" />
            </Button>
          ) : (
            <>
              <Button
                {...styles.subtotalDoneButton}
                aria-describedby={!allRequiredComplete ? 'customize-done-hint' : undefined}
                aria-disabled={!allRequiredComplete}
                id="customize-done-btn"
                isDisabled={!allRequiredComplete}
                tabIndex={allRequiredComplete ? 0 : -1}
                type="button"
                onClick={onDone}
              >
                <FormattedMessage defaultMessage="Done" id="customize_panel.done_button" />
              </Button>
              {!allRequiredComplete && (
                <Text className="sr-only" id="customize-done-hint">
                  <FormattedMessage
                    defaultMessage="Complete all required steps to enable Done"
                    id="customize_panel.footer.done_hint"
                  />
                </Text>
              )}
            </>
          )}
        </SubtotalFooter>
      )}
    </>
  );
};

StepsMainView.displayName = 'StepsMainView';

StepsMainView.propTypes = {
  activeStepId: PropTypes.string,
  bodyHeight: PropTypes.number,
  contentBodyStyles: PropTypes.object,
  coverGroups: PropTypes.array.isRequired,
  currency: PropTypes.string,
  enableLocking: PropTypes.bool,
  footerProduct: PropTypes.object,
  galleryPreviewProduct: PropTypes.object,
  hasUserInteracted: PropTypes.bool,
  isSimplePDP: PropTypes.bool,
  isStepComplete: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  liveMessage: PropTypes.string,
  onDone: PropTypes.func,
  onGalleryProductChange: PropTypes.func,
  onMainNext: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onSlotChoose: PropTypes.func.isRequired,
  onSlotProductPreview: PropTypes.func,
  onStepInteraction: PropTypes.func,
  onViewDetails: PropTypes.func,
  product: PropTypes.object.isRequired,
  stepsEverCompleteIds: PropTypes.arrayOf(PropTypes.string),
  scrollBodyRef: PropTypes.object.isRequired,
  selections: PropTypes.object,
  selectionSummary: PropTypes.string,
  showSubtotalFooter: PropTypes.bool,
  slotButtonRefs: PropTypes.object.isRequired,
  stepRefs: PropTypes.object.isRequired,
  steps: PropTypes.array.isRequired,
  styles: PropTypes.object.isRequired,
  subtotalListPrice: PropTypes.number,
  subtotalPrice: PropTypes.number,
};
